import { useMemo } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { findEntityByMobile } from 'api/entityApi'
import { ErrorState, PageLoader } from 'components/common/Loader'
import { useBrand } from 'config/brand'
import { useAsyncData } from 'hooks/useAsyncData'
import { decryptQueryParams } from 'utils/crypto'

/** A query value by any of its accepted names, ignoring case. */
function pick(params: Record<string, string>, ...names: string[]): string {
  const byLowerKey = new Map(Object.entries(params).map(([key, value]) => [key.toLowerCase(), value]))
  for (const name of names) {
    const value = byLowerKey.get(name.toLowerCase())
    if (value !== undefined && value.trim() !== '') return value.trim()
  }
  return ''
}

const digits = (value: string): string => value.replace(/\D/g, '')

/**
 * Opens the record a phone call belongs to:
 *   /view/call?countryCode=91&mobileNo=9876543210&entityType=lead&iscomefromcall=1&token=<SSO token>
 *
 * The call-event settings in Kit19 Sales build this link and the mobile app / CRM fill in the
 * caller's number. With entityType the search stays in that type; without it leads are searched
 * first, then enquiries. The record found opens in the normal view; when nothing matches the page
 * says so. Existing /view links are untouched - this route only answers call links.
 */
export default function CallEntityLookup() {
  useBrand('Caller')
  const location = useLocation()
  // RequireSession moves everything except the token into an encrypted q=, so read both forms.
  const params = useMemo(() => decryptQueryParams(location.search), [location.search])

  const fromCall = ['1', 'true', 'yes'].includes(pick(params, 'iscomefromcall').toLowerCase())
  // the alternative names other callers (the mobile app) send for the same three values
  const countryCode = pick(params, 'countryCode', 'cc', 'countrycode')
  const mobileNo = pick(params, 'mobileNo', 'mobile', 'mobileNumber', 'phone')
  const entityType = pick(params, 'entityType', 'entityName', 'entity_type').toLowerCase()
  const canSearch = fromCall && digits(mobileNo) !== ''

  const { data, loading, error, reload } = useAsyncData(
    (signal) => findEntityByMobile({ countryCode, mobileNo, entityType }, signal),
    [countryCode, mobileNo, entityType],
    { enabled: canSearch },
  )

  if (!canSearch) {
    return (
      <div className="app-shell">
        <div className="auth-error card">
          <h2>Nothing to look up</h2>
          <p>This link opens a record from a phone call and needs the caller&apos;s mobile number.</p>
        </div>
      </div>
    )
  }
  if (loading) return <PageLoader />
  if (error) {
    return (
      <div className="app-shell">
        <ErrorState message={error} onRetry={reload} />
      </div>
    )
  }
  if (data && data.entityId) {
    return <Navigate to={`/view/${data.entityType}/${encodeURIComponent(data.entityId)}`} replace />
  }

  const shownNumber = [digits(countryCode) ? `+${digits(countryCode)}` : '', mobileNo].filter(Boolean).join(' ')
  const lookedFor = entityType === 'lead' ? 'lead' : entityType === 'enquiry' ? 'enquiry' : 'lead or enquiry'
  return (
    <div className="app-shell">
      <div className="auth-error card">
        <h2>No record for this number</h2>
        <p>
          No {lookedFor} with the number <strong>{shownNumber}</strong> was found in your account.
        </p>
      </div>
    </div>
  )
}
